<template>
  <div class="">
    <div class="relative bg-white rounded-t-lg p-4">
      <div class="bg-white rounded-lg">
        <div class="py-6 px-4 sm:px-4">
          <h1 class="text-2xl mb-5 font-bold text-gray-900 truncate">
            Guide Profile
          </h1>
          <div class="flex items-center space-x-2">
            <div class="flex-shrink-0">
              <div class="relative">
                <img
                  class="h-32 w-32 rounded-full object-cover"
                  :src="profile.user.profile_photo_url"
                  :alt="profile.user.name"
                >
              </div>
            </div>
            <div>
              <h1 class="text-3xl font-bold text-gray-900">
                {{ profile.user.name }}
              </h1>
              <p
                v-if="!profile.team.personal_team"
                class="flex items-center text-xl font-medium text-gray-500"
              >
                <img
                  class="h-6 w-6 rounded-full object-cover mr-2"
                  :src="profile.team.photo_url"
                  :alt="profile.team.name"
                >
                {{ profile.team.name }}
              </p>
            </div>
          </div>
          <p class="text-sm font-medium text-gray-500 mt-3">
            About
          </p>
          <div class="">
            <editable-text-area
              :form="form"
              attribute="about"
              @submit.prevent="updateProfile"
            />
          </div>
        </div>
      </div>

      <div class="mt-5 px-4 border-t border-gray-200">
        <dl class="sm:divide-y sm:divide-gray-200">
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Visibility
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <div class="flex space-x-2 h-12 items-center">
                <span class="flex-shrink-0">Available as tour guide: </span>
                <editable-lightswitch
                  class="flex-shrink"
                  :form="form"
                  attribute="available_as_guide"
                  @submit.prevent="updateProfile"
                />
              </div>
              <div class="flex space-x-2 h-12 items-center">
                <span class="flex-shrink-0">Available as tour leader:</span>
                <editable-lightswitch
                  class="flex-shrink"
                  :form="form"
                  attribute="available_as_leader"
                  @submit.prevent="updateProfile"
                />
              </div>
            </dd>
            <dt class="text-sm font-medium text-gray-500">
              Destinations
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <ComboboxWithSecondaryText
                v-model="destinationForm.model"
                class="mt-2"
                has-multiple
                :disabled="availableDestinations.length < 1"
                :placeholder="availableDestinations.length > 0 ? 'Search for or select destinations' : 'There are no Destinations, please add some'"
                :items="availableDestinations"
                @update:modelValue="updateDestinations"
                @change="form.clearErrors('destinations')" />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Languages
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <template v-if="profile.languages.length > 0">
                <ul
                  role="list"
                  class="divide-y divide-gray-200"
                >
                  <li
                    v-for="language in profile.languages"
                    class="py-2 flex"
                  >
                    <span class="w-10">

                      <img
                        class="h-4 mt-1.5"
                        :src="language.image"
                        alt=""
                      >
                    </span>
                    <div class="flex flex-grow justify-between">
                      <div class="ml-3">
                        <p class="text-sm font-medium text-gray-900">
                          {{ language.name }}
                        </p>
                        <p class="text-xs text-gray-500">
                          {{ language.native }}
                        </p>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5 text-red-500 m-1 cursor-pointer hover:text-red-800"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        @click="removeLanguage(language.id)"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                  </li>
                </ul>
              </template>
              <p
                v-else
                class="text-gray-500 italic"
              >
                No languages added yet
              </p>
              <p
                class="text-indigo-500 cursor-pointer mt-3 hover:text-indigo-900"
                @click="showAddLanguageModal = true"
              >
                Add language
              </p>
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Email address
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-field
                :form="form"
                attribute="email"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Gender
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-select
                :form="form"
                attribute="gender"
                :options="genders"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Date of birth
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-date-picker
                :form="form"
                attribute="dob"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Type of tours preferred
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-field
                :form="form"
                attribute="tour_types"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Area and attraction knowledge
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-text-area
                :form="form"
                attribute="knowledge"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Experience
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-text-area
                :form="form"
                attribute="experience"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
          <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt class="text-sm font-medium text-gray-500">
              Accepted payment methods
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <editable-checkbox-multi-select
                :form="form"
                attribute="payment_options"
                :options="payment_methods"
                @submit.prevent="updateProfile"
              />
            </dd>
          </div>
        </dl>
      </div>
    </div>

    <div
      v-if="false"
      class="bg-white p-4 mx-0"
    >
      <div class="relative mt-10 grid grid-cols-1 gap-x-14 md:grid-cols-2">
        <button
          type="button"
          class="absolute -top-1 -left-1.5 flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
        >
          <span class="sr-only">Previous month</span>
          <ChevronLeftIcon
            class="h-5 w-5"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          class="absolute -top-1 -right-1.5 flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
        >
          <span class="sr-only">Next month</span>
          <ChevronRightIcon
            class="h-5 w-5"
            aria-hidden="true"
          />
        </button>
        <section
          v-for="(month, monthIdx) in months"
          :key="monthIdx"
          :class="[monthIdx === months.length - 1 && 'hidden md:block', 'text-center']"
        >
          <h2 class="font-semibold text-gray-900">
            {{ month.name }}
          </h2>
          <div class="mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
            <div>M</div>
            <div>T</div>
            <div>W</div>
            <div>T</div>
            <div>F</div>
            <div>S</div>
            <div>S</div>
          </div>
          <div
            class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
          >
            <button
              v-for="(day, dayIdx) in month.days"
              :key="day.date"
              type="button"
              :class="[day.isCurrentMonth ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-400', dayIdx === 0 && 'rounded-tl-lg', dayIdx === 6 && 'rounded-tr-lg', dayIdx === month.days.length - 7 && 'rounded-bl-lg', dayIdx === month.days.length - 1 && 'rounded-br-lg', 'relative py-1.5 hover:bg-gray-100 focus:z-10']"
            >
              <time
                :datetime="day.date"
                :class="[day.isToday && 'bg-indigo-600 font-semibold text-white', 'mx-auto flex h-7 w-7 items-center justify-center rounded-full']"
              >
                {{ day.date.split('-').pop().replace(/^0/, '') }}
              </time>
            </button>
          </div>
        </section>
      </div>
    </div>

    <div
      v-if="!editing"
      class="bg-white rounded-b-lg p-4"
    >
      <div>
        <h2 class="sr-only">
          Customer Reviews
        </h2>

        <div class="-my-10">
          <div
            v-for="(review, reviewIdx) in profile.reviews"
            :key="review.id"
            class="flex text-sm text-gray-500 space-x-4"
          >
            <div class="flex-none py-10">
              <img
                :src="review.created_by.profile_photo_url"
                alt=""
                class="w-10 h-10 bg-gray-100 rounded-full"
              >
            </div>
            <div :class="[reviewIdx === 0 ? '' : 'border-t border-gray-200', 'flex-1 py-10']">
              <h3 class="font-medium text-gray-900">
                {{ review.created_by.name }}
              </h3>
              <p>
                <time :datetime="review.created_at">{{ review.date }}</time>
              </p>

              <div class="flex items-center mt-4">
                <star-rating
                  :rating="review.rating"
                  :read-only="true"
                  :star-size="14"
                  :show-rating="false"
                />
              </div>
              <p class="sr-only">
                {{ review.rating }} out of 5 stars
              </p>

              <div
                class="mt-4 prose prose-sm max-w-none text-gray-500"
                v-html="review.comment"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <LargeModal
    :show="showAddLanguageModal"
    title="Add Language"
    subtitle="Add a language to your profile"
    @close="closeAddLanguageModal()"
  >
    <template #default>
      <LanguageSelectPicker ref="languageSelectPickerReference" v-model="languageForm.model" />
    </template>

    <template #footer>
      <create-button
        class="mr-3"
        @click="addLanguage"
      >
        Add
      </create-button>
      <cancel-button
        @click="closeAddLanguageModal()"
      >
        Cancel
      </cancel-button>
    </template>
  </LargeModal>
</template>

<script>
import {
  ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/vue/24/solid';
import EditableField from '../../ComponentsDeprecated/EditableField.vue';
import EditableTextArea from '../../ComponentsDeprecated/EditableTextArea.vue';
import EditableSelect from '../../ComponentsDeprecated/EditableSelect.vue';
import EditableLightswitch from '@/ComponentsDeprecated/EditableLightswitch';
import EditableDatePicker from '@/ComponentsDeprecated/EditableDatePicker';
import EditableCheckboxMultiSelect from '@/ComponentsDeprecated/EditableCheckboxMultiSelect';
import LargeModal from '@/ComponentsDeprecated/Modal/LargeModal';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton';
import CancelButton from '@/ComponentsDeprecated/Buttons/CancelButton';
import LanguageSelectPicker from '@/ComponentsDeprecated/Select/LanguageSelectPicker';
import ComboboxWithSecondaryText from '@/Components/Forms/ComboboxWithSecondaryText.vue';
import { reactive } from 'vue';

export default {
  name: 'GuideProfile',
  components: {
    ComboboxWithSecondaryText,
    LanguageSelectPicker,
    LargeModal,
    EditableDatePicker,
    EditableLightswitch,
    EditableSelect,
    ChevronLeftIcon,
    ChevronRightIcon,
    EditableField,
    EditableTextArea,
    EditableCheckboxMultiSelect,
    CreateButton,
    CancelButton,
  },
  props: {
    profile: {
      required: true,
      type: Object,
    },
    languages: {
      required: false,
      default: [],
      type: Array,
    },
    genders: {
      required: true,
      type: Object,
    },
    payment_methods: {
      required: true,
      type: Object,
    },
    destinations: {
      required: false,
      type: Array,
      default: []
    }
  },

  data() {
    return {
      showAddLanguageModal: false,
      languageForm: this.$inertia.form({
        model: null,
      }),
      destinationForm: {
        model: [],
      },
      form: this.$inertia.form({
        about: this.profile.about,
        email: this.profile.email,
        tour_types: this.profile.tour_types,
        knowledge: this.profile.knowledge,
        experience: this.profile.experience,
        available_as_guide: this.profile.available_as_guide,
        available_as_leader: this.profile.available_as_leader,
        gender: this.profile.gender,
        dob: this.profile.dob,
        payment_options: this.profile.payment_options,
      })
    };
  },

  computed: {
    availableDestinations() {
      return this.destinations.map((destination) => ({
        id: destination.id,
        name: destination.name,
        parent: destination.parent,
        primaryText: destination.name,
        secondaryText: destination.parent?.name ?? destination.area ?? '',
        selected: this.profile.places.some((place) => place.id === destination.id),
      }))
        .sort(
          (a, b) => (b.name - a.name),
        );
    },

    selectedDestinations() {
      return this.profile.places.map((destination) => ({
        id: destination.id,
        name: destination.name,
        parent: destination.parent,
        primaryText: destination.name,
        secondaryText: destination.parent?.name ?? destination.area ?? '',
      }))
        .sort(
          (a, b) => (b.name - a.name),
        );
    },
  },

  watch: {},
  mounted() {
    this.languageForm.model = this.languages[0];
    this.destinationForm.model = this.selectedDestinations;
  },

  methods: {
    updateDestinations(destinations) {
      this.destinationForm.model = destinations;
      this.$inertia.put(route('user.profile.syncDestinations'), {
        destinations: destinations.map((a) => a.id),
      });
    },
    addLanguage() {
      this.languageForm.post(route('user.profile.add.language', {
        guideProfile: this.profile.id,
        language: this.languageForm.model.id,
      }), {
        errorBag: 'addLanguage',
        preserveScroll: true,
        onSuccess: () => {
          this.$notify({
            type: 'success',
            title: 'Added',
            text: 'Language added',
          }, 4000);

          this.languageForm.reset();
          this.$refs.languageSelectPickerReference.removeSelected()
          this.closeAddLanguageModal();
        },

        onError: () => {
          this.$notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not add language',
          }, 4000);
        },
      });
    },
    removeLanguage(id) {
      this.$inertia.delete(route('user.profile.remove.language', {
        guideProfile: this.profile.id,
        language: id,
      }));
    },
    closeAddLanguageModal() {
      this.showAddLanguageModal = false;
    },
    updateProfile() {
      this.form.put(route('user.profile.guide.update'), {
        errorBag: 'updateProfile',
        preserveScroll: true,
        preserveState: true,
      });
    },
  },
};
</script>
